import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getBrokers } from "../lib/api";
import { BrokerLogo } from "../lib/logo.jsx";
import { Seo } from "../components/Seo";
import { Search, Phone, Mail, Globe, ArrowRight, Star } from "lucide-react";

const shortName = (name) => name.replace(/MENKUL.*/i, "").replace("A.Ş.", "").trim();

const AllBrokers = () => {
  const [params, setParams] = useSearchParams();
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(params.get("q") || "");
  const [onlyPopular, setOnlyPopular] = useState(false);

  useEffect(() => {
    getBrokers().then((d) => setBrokers(d.brokers)).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return brokers.filter((b) => {
      if (onlyPopular && !b.is_popular) return false;
      if (!q) return true;
      return b.name.toLowerCase().includes(q) || (b.phone || "").toLowerCase().includes(q) || (b.email || "").toLowerCase().includes(q);
    });
  }, [brokers, query, onlyPopular]);

  const onSearch = (v) => { setQuery(v); setParams(v ? { q: v } : {}); };

  return (
    <div className="paper-texture">
      <Seo title="Tüm Aracı Kurumlar | SPK Lisanslı Borsa Aracı Kurumları Listesi"
        description="Türkiye'deki tüm SPK lisanslı aracı kurumların telefon, e-posta, adres ve web sitesi bilgileri. Arayın ve karşılaştırın."
        path="/araci-kurumlar" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="max-w-2xl border-b-2 border-ink pb-6">
          <span className="kicker text-claret">Rehber</span>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl">Tüm SPK Lisanslı Aracı Kurumlar</h1>
          <p className="mt-3 font-display text-lg text-muted-foreground">
            {brokers.length || "69"} yetkili aracı kurumun iletişim bilgileri tek listede. Arayın, karşılaştırın, doğru kuruma ulaşın.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 border-b-2 border-ink pb-1">
            <Search size={18} className="text-ink" />
            <input value={query} onChange={(e) => onSearch(e.target.value)} placeholder="Kurum adı, telefon veya e-posta ara..."
              className="h-9 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-muted-foreground" data-testid="broker-search-input" />
          </div>
          <button onClick={() => setOnlyPopular((v) => !v)}
            className={`inline-flex items-center justify-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold transition-colors ${
              onlyPopular ? "bg-ink text-paper" : "border border-ink text-ink hover:bg-ink hover:text-paper"
            }`} data-testid="filter-popular-button">
            <Star size={15} /> Popülerler
          </button>
        </div>

        <div className="mt-4 kicker text-muted-foreground" data-testid="broker-count">
          {loading ? "Yükleniyor..." : `${filtered.length} kurum listeleniyor`}
        </div>

        <div className="mt-4 grid w-full min-w-0 overflow-hidden border-l border-t border-hair md:grid-cols-2">
          {filtered.map((b) => (
            <div key={b.slug} className="flex min-w-0 flex-col gap-4 border-b border-r border-hair bg-card p-5 transition-colors hover:bg-tint" data-testid={`broker-row-${b.slug}`}>
              <div className="flex items-start gap-4">
                <BrokerLogo slug={b.slug} name={b.name} size={52} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate font-display text-lg font-semibold text-ink">{shortName(b.name)}</h3>
                    {b.is_popular && <Star size={14} className="shrink-0 text-claret" fill="currentColor" />}
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{b.name}</p>
                </div>
              </div>
              <div className="grid gap-2 text-sm">
                {b.phone && (
                  <a href={`tel:${b.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-ink/80 hover:text-claret" data-testid={`broker-phone-${b.slug}`}>
                    <Phone size={14} className="text-claret" /> {b.phone}
                  </a>
                )}
                {b.email && (
                  <a href={`mailto:${b.email}`} className="flex items-center gap-2 truncate text-ink/80 hover:text-claret">
                    <Mail size={14} className="text-muted-foreground" /> <span className="truncate">{b.email}</span>
                  </a>
                )}
                {b.website && (
                  <a href={`https://${b.domain}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 truncate text-ink/80 hover:text-claret">
                    <Globe size={14} className="text-muted-foreground" /> <span className="truncate">{b.website}</span>
                  </a>
                )}
              </div>
              <Link to={`/kurum/${b.slug}`} className="mt-auto inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-claret underline-offset-4 hover:underline" data-testid={`broker-detail-link-${b.slug}`}>
                Detay & tüm bilgiler <ArrowRight size={15} />
              </Link>
            </div>
          ))}
        </div>

        {!loading && filtered.length === 0 && (
          <div className="mt-10 border border-dashed border-hair p-10 text-center text-muted-foreground">Aramanızla eşleşen aracı kurum bulunamadı.</div>
        )}
      </div>
    </div>
  );
};

export default AllBrokers;
